import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Inbox, Unlock, Wallet, BarChart3, ArrowLeft, ChevronDown, CreditCard, User
} from 'lucide-react';
import { appName } from '../../config/branding';
import VerificationResubmit from './VerificationResubmit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { path: 'leads', label: 'Available Leads', icon: Inbox },
  { path: 'my-leads', label: 'My Leads', icon: Unlock },
  { path: 'wallet', label: 'Wallet', icon: Wallet },
  { path: 'performance', label: 'Performance', icon: BarChart3 },
  { path: 'complete-profile', label: 'Complete Profile', icon: User },
];

const DashboardLayout = () => {
  const { proId } = useParams();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (proId) fetchPro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proId]);

  const fetchPro = async () => {
    try {
      const res = await axios.get(`${API}/professionals/${proId}`);
      setPro(res.data);
    } catch (e) {
      console.error('Error fetching professional:', e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 md:translate-x-0 md:static md:flex-shrink-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-gray-100">
            <a href="/" className="inline-block">
              <svg width="160" height="28" viewBox="0 0 160 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <text x="0" y="22" fontFamily="Inter, sans-serif" fontSize="18" fontWeight="800" fill="#37b34a">{appName}</text>
              </svg>
            </a>
            <p className="text-xs text-gray-400 mt-1">Pro Dashboard</p>
          </div>

          {/* Profile Card */}
          {pro && (
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <img src={pro.image} alt={pro.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{pro.name}</p>
                  <p className="text-xs text-gray-500 truncate">{pro.primary_service}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                <CreditCard size={14} className="text-amber-600" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-amber-700">
                      {(pro.free_credits_balance || 0) + (pro.paid_credits_balance || 0)}
                    </span>
                    <span className="text-xs text-amber-600">credits</span>
                  </div>
                  <div className="text-xs text-amber-600">
                    {pro.free_credits_balance || 0} free + {pro.paid_credits_balance || 0} paid
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={`/dashboard/${proId}/${path}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#37b34a]/10 text-[#37b34a]'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                <Icon size={18} />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Back */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors w-full"
            >
              <ArrowLeft size={16} />
              Back to home
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top bar (mobile) */}
        <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-1 text-gray-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-gray-900">{pro?.name || 'Dashboard'}</span>
        </div>

        <div className="p-4 md:p-8 max-w-[1100px]">
          {pro && pro.verification_status === 'pending' && (
            <div data-testid="verification-pending-banner" className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl">
              <p className="font-semibold">Identity verification pending</p>
              <p className="text-amber-700 mt-0.5">We're reviewing your ID document and selfie — usually within 24-48 hours. Lead unlocking turns on automatically once approved.</p>
            </div>
          )}
          {pro && pro.verification_status === 'rejected' && (
            <VerificationResubmit pro={pro} proId={proId} onResubmitted={fetchPro} />
          )}
          <Outlet context={{ pro, proId }} />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
